import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Heart, Share2, MessageCircle, CheckCircle, ArrowLeft, Users, Calendar, ExternalLink, Bookmark, BookmarkCheck, Flag, Ban, Star, Pencil, Camera, X as XIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import ImageLightbox from '../components/ImageLightbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import ShareButtons from '../components/ShareButtons';
import DonateModal from '../components/DonateModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportDialog = ({ postId, token, language, onClose }) => {
  const [reason, setReason] = useState('inappropriate');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const reasons = [
    { value: 'inappropriate', label: language === 'it' ? 'Contenuto inappropriato' : 'Inappropriate content' },
    { value: 'scam', label: language === 'it' ? 'Possibile truffa' : 'Possible scam' },
    { value: 'spam', label: 'Spam' },
    { value: 'harassment', label: language === 'it' ? 'Molestie' : 'Harassment' },
    { value: 'other', label: language === 'it' ? 'Altro' : 'Other' },
  ];

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/posts/${postId}/report`, { reason, description }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'it' ? 'Segnalazione inviata. Grazie!' : 'Report sent. Thank you!');
      onClose();
    } catch {
      toast.error(language === 'it' ? 'Errore nell\'invio della segnalazione' : 'Error sending report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="report-dialog">
      <p className="text-sm text-stone-600">
        {language === 'it' ? 'Seleziona il motivo della segnalazione:' : 'Select the reason for reporting:'}
      </p>
      <div className="space-y-2">
        {reasons.map((r) => (
          <button
            key={r.value}
            onClick={() => setReason(r.value)}
            className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all text-sm ${
              reason === r.value ? 'border-red-500 bg-red-50 text-red-800' : 'border-stone-200 hover:border-stone-300 text-stone-700'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>
      <textarea
        data-testid="report-description-input"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={language === 'it' ? 'Descrivi il problema (opzionale)...' : 'Describe the issue (optional)...'}
        className="w-full p-3 border border-stone-200 rounded-xl resize-none text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
        rows={3}
      />
      <Button
        data-testid="submit-report-btn"
        onClick={handleSubmit}
        disabled={loading}
        className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl"
      >
        {loading ? '...' : (language === 'it' ? 'Invia segnalazione' : 'Submit report')}
      </Button>
    </div>
  );
};

const PostDetail = () => {
  const { postId } = useParams();
  const { isAuthenticated, user, token } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [donations, setDonations] = useState([]);
  const [fundUpdates, setFundUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isFavorited, setIsFavorited] = useState(false);
  const [newUpdate, setNewUpdate] = useState('');
  const [postingUpdate, setPostingUpdate] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  useEffect(() => {
    fetchPost();
    fetchDonations();
    fetchCategories();
    fetchFundUpdates();
  }, [postId]);

  useEffect(() => {
    if (user && post) {
      setIsFavorited(user.favorites?.includes(post.post_id) || false);
    }
  }, [user, post]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posts/${postId}`);
      setPost(response.data);
    } catch {
      toast.error(language === 'it' ? 'Errore nel caricamento' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDonations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/donations/post/${postId}`);
      setDonations(response.data);
    } catch { /* ignore */ }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.categories);
    } catch { /* ignore */ }
  };

  const fetchFundUpdates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posts/${postId}/updates`);
      setFundUpdates(response.data);
    } catch { /* ignore */ }
  };

  const handlePostUpdate = async () => {
    if (!newUpdate.trim()) return;
    setPostingUpdate(true);
    try {
      await axios.post(`${API_URL}/api/posts/${postId}/updates`, { content: newUpdate }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewUpdate('');
      fetchFundUpdates();
      toast.success(t('success'));
    } catch { toast.error(t('error')); }
    finally { setPostingUpdate(false); }
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? (language === 'it' ? cat.name_it : cat.name_en) : catId;
  };

  const handleShare = () => {
    const shareUrl = `${API_URL}/api/share/post/${post.post_id}`;
    const sharePrefix = "Buongiorno cari amici non scorrete subito via ma guardate: ";
    if (navigator.share) {
      navigator.share({ title: post.title, text: `${sharePrefix}${post.title}`, url: shareUrl });
    } else {
      navigator.clipboard.writeText(`${sharePrefix}${shareUrl}`);
      toast.success(language === 'it' ? 'Link copiato!' : 'Link copied!');
    }
  };

  const handleFavorite = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      if (isFavorited) {
        await axios.delete(`${API_URL}/api/posts/${post.post_id}/favorite`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorited(false);
        toast.success(language === 'it' ? 'Rimosso dai preferiti' : 'Removed from favorites');
      } else {
        await axios.post(`${API_URL}/api/posts/${post.post_id}/favorite`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorited(true);
        toast.success(language === 'it' ? 'Aggiunto ai preferiti' : 'Added to favorites');
      }
    } catch {
      toast.error(language === 'it' ? 'Errore' : 'Error');
    }
  };

  const handleBlockUser = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      await axios.post(`${API_URL}/api/users/${post.user_id}/block`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'it' ? 'Utente bloccato' : 'User blocked');
    } catch {
      toast.error(language === 'it' ? 'Errore' : 'Error');
    }
  };

  const handleStartChat = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      const response = await axios.post(
        `${API_URL}/api/conversations`,
        { user_id: post.user_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/chat', { state: { conversationId: response.data.conversation_id } });
    } catch {
      toast.error(language === 'it' ? 'Errore nell\'avvio della chat' : 'Error starting chat');
    }
  };

  const handleHighlight = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      await axios.post(`${API_URL}/api/posts/${post.post_id}/highlight`, { days: 7 }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'it' ? 'Post messo in evidenza per 7 giorni!' : 'Post highlighted for 7 days!');
      fetchPost();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'it' ? 'Errore' : 'Error'));
    }
  };

  const handleEditPost = () => {
    setEditData({
      title: post.title,
      description: post.description,
      amount_needed: post.amount_needed,
      currency: post.currency,
      category: post.category,
      images: post.images || [],
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editData.title || !editData.description || !editData.images?.length) {
      toast.error(language === 'it' ? 'Titolo, descrizione e almeno un\'immagine sono obbligatori' : 'Title, description and at least one image are required');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/posts/${post.post_id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'it' ? 'Post aggiornato!' : 'Post updated!');
      setShowEditDialog(false);
      fetchPost();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Confetti on goal reached
  const progress = post ? (post.amount_raised / post.amount_needed) * 100 : 0;
  const goalReached = progress >= 100;
  
  useEffect(() => {
    if (goalReached && post) {
      import('../utils/confetti').then(m => m.default());
    }
  }, [goalReached, post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Navbar />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h2 className="font-heading text-2xl font-semibold text-stone-700">
            {language === 'it' ? 'Richiesta non trovata' : 'Request not found'}
          </h2>
          <Link to="/feed"><Button className="mt-4">{language === 'it' ? 'Torna al feed' : 'Back to feed'}</Button></Link>
        </div>
      </div>
    );
  }

  const formattedDate = new Date(post.created_at).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
    day: 'numeric', month: 'long', year: 'numeric'
  });
  const isOwnPost = user?.user_id === post.user_id;

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      {lightboxIdx >= 0 && post?.images && <ImageLightbox images={post.images} initialIndex={lightboxIdx} onClose={() => setLightboxIdx(-1)} />}
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-16">
        {/* Back Button */}
        <Link to="/feed" className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-6">
          <ArrowLeft className="h-5 w-5 mr-2" />
          {t('back')} {language === 'it' ? 'al feed' : 'to feed'}
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Images */}
            {post.images && post.images.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-stone-200"
              >
                <img src={post.images[0]} alt={post.title} className="w-full aspect-video object-cover cursor-pointer hover:opacity-90 transition-opacity" onClick={() => setLightboxIdx(0)} />
                {post.images.length > 1 && (
                  <div className="p-4 flex gap-2 overflow-x-auto">
                    {post.images.slice(1).map((img, i) => (
                      <img key={i} src={img} alt={`${post.title} ${i + 2}`} className="h-20 w-20 rounded-lg object-cover flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setLightboxIdx(i + 1)} />
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Video */}
            {post.video_url && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-stone-200"
              >
                <video src={post.video_url} controls className="w-full aspect-video" />
              </motion.div>
            )}

            {/* Post Content */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                  {getCategoryName(post.category)}
                </Badge>
                {post.is_verified && (
                  <Badge className="bg-blue-100 text-blue-700">
                    <CheckCircle className="h-3 w-3 mr-1" />{t('verified')}
                  </Badge>
                )}
                {post.is_highlighted && (
                  <Badge className="bg-amber-100 text-amber-700">
                    <Star className="h-3 w-3 mr-1" />{language === 'it' ? 'In evidenza' : 'Featured'}
                  </Badge>
                )}
              </div>
              <h1 className="font-heading text-3xl font-bold text-stone-900 mb-4">{post.title}</h1>
              {isOwnPost && (
                <Button variant="outline" size="sm" onClick={handleEditPost} data-testid="edit-post-btn"
                  className="mb-4 rounded-full text-stone-600 border-stone-300">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />{language === 'it' ? 'Modifica post' : 'Edit post'}
                </Button>
              )}
              <div className="flex items-center gap-2 text-stone-500 mb-6">
                <Calendar className="h-4 w-4" /><span>{formattedDate}</span>
              </div>
              <p className="text-stone-700 leading-relaxed whitespace-pre-wrap">{post.description}</p>
            </motion.div>

            {/* Donations List */}
            {donations.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8"
              >
                <h3 className="font-heading text-xl font-semibold text-stone-900 mb-6">
                  {t('donations')} ({donations.length})
                </h3>
                <div className="space-y-4">
                  {donations.slice(0, 5).map((donation) => (
                    <div key={donation.donation_id} className="flex items-center gap-4 p-3 rounded-xl bg-stone-50">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={`https://api.dicebear.com/9.x/lorelei/svg?seed=${donation.donor_id}`} />
                        <AvatarFallback>{donation.donor_name[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-medium text-stone-900">
                          {donation.anonymous ? (language === 'it' ? 'Anonimo' : 'Anonymous') : donation.donor_name}
                        </p>
                        {donation.message && <p className="text-sm text-stone-500">{donation.message}</p>}
                      </div>
                      <p className="font-semibold text-emerald-800">{donation.amount.toFixed(2)} {donation.currency}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Fund Usage Updates */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8"
            >
              <h3 className="font-heading text-xl font-semibold text-stone-900 mb-6">
                {t('fundUpdates')} ({fundUpdates.length})
              </h3>

              {/* Post update form (only for post author) */}
              {isAuthenticated && isOwnPost && (
                <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <textarea
                    data-testid="fund-update-input"
                    value={newUpdate}
                    onChange={(e) => setNewUpdate(e.target.value)}
                    placeholder={t('updatePlaceholder')}
                    className="w-full p-3 bg-white border border-amber-200 rounded-xl resize-none text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                    rows={3}
                  />
                  <Button data-testid="post-update-btn" onClick={handlePostUpdate} disabled={postingUpdate || !newUpdate.trim()}
                    className="mt-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm">
                    {postingUpdate ? '...' : t('postUpdate')}
                  </Button>
                </div>
              )}

              {fundUpdates.length === 0 ? (
                <p className="text-stone-500 text-center py-6">
                  {language === 'it' ? 'Nessun aggiornamento ancora' : 'No updates yet'}
                </p>
              ) : (
                <div className="space-y-4">
                  {fundUpdates.map((upd) => (
                    <div key={upd.update_id} className="p-4 rounded-xl bg-stone-50 border-l-4 border-amber-500">
                      <p className="text-stone-700">{upd.content}</p>
                      <p className="text-xs text-stone-400 mt-2">
                        {upd.user_name} — {new Date(upd.created_at).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Donation Progress Card */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 lg:sticky lg:top-24"
            >
              <div className="mb-6">
                <div className="flex justify-between items-end mb-2">
                  <div>
                    <p className="text-sm text-stone-500">{t('raised')}</p>
                    <p className="font-heading text-3xl font-bold text-emerald-800">
                      {post.amount_raised.toFixed(2)} <span className="text-lg">{post.currency}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-stone-500">{t('goal')}</p>
                    <p className="font-semibold text-stone-700">{post.amount_needed.toFixed(2)} {post.currency}</p>
                  </div>
                </div>
                <Progress value={Math.min(progress, 100)} className="h-3 bg-stone-100" />
                {goalReached ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mt-3 text-center" data-testid="goal-reached-banner">
                    <p className="text-emerald-700 font-semibold text-sm">
                      {language === 'it' ? 'Obiettivo raggiunto! Grazie a tutti i donatori!' : 'Goal reached! Thanks to all donors!'}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-stone-500 mt-2">{Math.round(progress)}% {language === 'it' ? 'completato' : 'completed'}</p>
                )}
              </div>

              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-stone-100">
                <div className="flex items-center gap-2 text-stone-600">
                  <Users className="h-5 w-5" /><span>{post.donors_count} {t('donors')}</span>
                </div>
              </div>

              {/* Donate Button */}
              <Dialog open={showDonateModal} onOpenChange={setShowDonateModal}>
                <DialogTrigger asChild>
                  <Button data-testid="donate-btn"
                    className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-lg font-bold uppercase tracking-wide shadow-lg shadow-amber-500/30 hover:shadow-amber-500/40 transition-all hover:scale-[1.02]"
                  >
                    <Heart className="h-5 w-5 mr-2" fill="currentColor" />{t('donateNow')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md" aria-describedby="donate-dialog-desc">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">{t('donate')}</DialogTitle>
                    <p id="donate-dialog-desc" className="sr-only">{t('donateNow')}</p>
                  </DialogHeader>
                  <DonateModal post={post} onClose={() => setShowDonateModal(false)} />
                </DialogContent>
              </Dialog>

              {/* Social Share + Favorite */}
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-stone-600">{language === 'it' ? 'Condividi' : 'Share'}</span>
                  <ShareButtons
                    url={`${window.location.origin}/posts/${post.post_id}`}
                    title={post.title}
                    description={post.description?.substring(0, 100)}
                    language={language}
                  />
                </div>
                <Button data-testid="favorite-btn" variant="outline" 
                  className={`rounded-xl w-full ${isFavorited ? 'border-emerald-500 text-emerald-700 bg-emerald-50' : 'border-stone-200'}`}
                  onClick={handleFavorite}
                >
                  {isFavorited ? <BookmarkCheck className="h-4 w-4 mr-2" /> : <Bookmark className="h-4 w-4 mr-2" />}
                  {isFavorited ? (language === 'it' ? 'Salvato' : 'Saved') : (language === 'it' ? 'Salva' : 'Save')}
                </Button>
              </div>

              {/* Chat / Report / Block */}
              <div className="mt-4 space-y-2">
                {isAuthenticated && !isOwnPost && (
                  <Button data-testid="chat-btn" className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white h-12 font-semibold" onClick={handleStartChat}>
                    <MessageCircle className="h-5 w-5 mr-2" />{t('chat')}
                  </Button>
                )}
                {isAuthenticated && isOwnPost && !post.is_highlighted && (
                  <Button data-testid="highlight-btn" variant="outline" className="w-full rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50" onClick={handleHighlight}>
                    <Star className="h-4 w-4 mr-2" />{language === 'it' ? 'Metti in evidenza' : 'Highlight'}
                  </Button>
                )}
                {isAuthenticated && !isOwnPost && (
                  <div className="flex gap-2">
                    <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
                      <DialogTrigger asChild>
                        <Button data-testid="report-btn" variant="ghost" className="flex-1 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-xl text-sm">
                          <Flag className="h-4 w-4 mr-1" />{language === 'it' ? 'Segnala' : 'Report'}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle className="text-red-600">
                            {language === 'it' ? 'Segnala post' : 'Report post'}
                          </DialogTitle>
                        </DialogHeader>
                        <ReportDialog postId={post.post_id} token={token} language={language} onClose={() => setShowReportDialog(false)} />
                      </DialogContent>
                    </Dialog>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button data-testid="block-btn" variant="ghost" className="flex-1 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-xl text-sm">
                          <Ban className="h-4 w-4 mr-1" />{language === 'it' ? 'Blocca' : 'Block'}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{language === 'it' ? 'Bloccare questo utente?' : 'Block this user?'}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {language === 'it' 
                              ? 'Non vedrai più i post di questo utente nel feed. Puoi sbloccare in qualsiasi momento dalle impostazioni.'
                              : 'You will no longer see posts from this user in the feed. You can unblock anytime from settings.'}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{language === 'it' ? 'Annulla' : 'Cancel'}</AlertDialogCancel>
                          <AlertDialogAction onClick={handleBlockUser} className="bg-red-600 hover:bg-red-700 text-white">
                            {language === 'it' ? 'Blocca' : 'Block'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>

              {/* Author Card */}
              <div className="mt-6 pt-6 border-t border-stone-100">
                <Link to={`/profile/${post.user_id}`} className="flex items-center gap-3 hover:bg-stone-50 -mx-3 p-3 rounded-xl transition-colors">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={post.user_picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${post.user_id}`} />
                    <AvatarFallback>{post.user_name[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold text-stone-900">{post.user_name}</p>
                    <p className="text-sm text-stone-500">{language === 'it' ? 'Vedi profilo' : 'View profile'}</p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-stone-400" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Edit Post Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{language === 'it' ? 'Modifica richiesta' : 'Edit request'}</DialogTitle>
          </DialogHeader>
          {editData && (
            <EditPostForm editData={editData} setEditData={setEditData} language={language}
              categories={categories} onSave={handleSaveEdit} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EditPostForm = ({ editData, setEditData, language, categories, onSave }) => {
  const imageInputRef = useRef(null);
  const it = language === 'it';

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if ((editData.images?.length || 0) + files.length > 5) { toast.error(it ? 'Massimo 5 immagini' : 'Maximum 5 images'); return; }
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) return;
      const reader = new FileReader();
      reader.onloadend = () => setEditData(prev => ({ ...prev, images: [...(prev.images || []), reader.result] }));
      reader.readAsDataURL(file);
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="font-semibold text-sm">{it ? 'Titolo' : 'Title'}</label>
        <input data-testid="edit-post-title" className="w-full p-3 border rounded-xl text-sm" value={editData.title}
          onChange={e => setEditData(prev => ({ ...prev, title: e.target.value }))} />
      </div>
      <div className="space-y-2">
        <label className="font-semibold text-sm">{it ? 'Descrizione' : 'Description'}</label>
        <textarea data-testid="edit-post-description" className="w-full p-3 border rounded-xl text-sm min-h-[120px]" value={editData.description}
          onChange={e => setEditData(prev => ({ ...prev, description: e.target.value }))} />
      </div>
      <div className="space-y-2">
        <label className="font-semibold text-sm">{it ? 'Foto * (max 5)' : 'Photos * (max 5)'}</label>
        <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" ref={imageInputRef} />
        <div className="flex flex-wrap gap-3">
          {(editData.images || []).map((img, i) => (
            <div key={i} className="relative group">
              <img src={img} alt="" className="h-20 w-20 object-cover rounded-xl border-2 border-stone-200" />
              <button type="button" onClick={() => setEditData(prev => ({ ...prev, images: prev.images.filter((_, idx) => idx !== i) }))}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <XIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
          {(editData.images?.length || 0) < 5 && (
            <button type="button" onClick={() => imageInputRef.current?.click()}
              className="h-20 w-20 rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center text-stone-400 hover:border-amber-500 hover:text-amber-600 transition-colors">
              <Camera className="h-5 w-5 mb-1" /><span className="text-[10px]">{it ? 'Aggiungi' : 'Add'}</span>
            </button>
          )}
        </div>
      </div>
      <Button onClick={onSave} data-testid="save-edit-post-btn"
        className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold">
        {it ? 'Salva modifiche' : 'Save changes'}
      </Button>
    </div>
  );
};

export default PostDetail;
